import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProfileService } from '../../services/profile.service';
import { PackageService } from '../../services/package.service';
import { ApiService } from '../../services/api.service';
import { Subscription, forkJoin } from 'rxjs';
import {
  syncLocationSelections as syncLocationSelectionsUtil,
  subscribeToLocationsForFilterModal,
} from '../../shared/utils/filter-location.util';
import { Checkbox } from '../../shared/components/checkbox';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions/modal-footer-actions.component';

export interface FilterOptions {
  services: string[];
  employees: string[];
  locations: string[];
  statuses: string[];
}

export interface FilterItem {
  value: string;
  label: string;
  checked: boolean;
}

/**
 * Modal component that provides a configurable filter UI for appointments — allows selecting services, employees, locations, and statuses to refine appointment lists.
 * Purpose: present and manage filter options, sync with incoming active filters and toolbar location selections, and emit user filter actions (apply / clear / close).
 * Key methods:
 *   - loadLocations(): subscribes to profile/location data and populates location filter items.
 *   - loadPackages(): loads available service packages (differs for admin vs staff) and populates service filter items.
 *   - loadEmployees(): fetches employees for each accessible location, deduplicates and populates employee filter items.
 *   - syncWithActiveFilters(): applies an externally-provided FilterOptions object to current UI selections.
 *   - syncLocationSelections(): updates location selections to match toolbar-provided labels.
 *   - onApplyFilters(): builds a FilterOptions object from selected items and emits applyFilters.
 *   - onClearFilters(): clears all selections and emits clearFilters.
 *   - onClose() / onCancel(): emit close to request modal dismissal.
 *   - onBackdropClick(event): closes modal when backdrop is clicked.
 *   - hasActiveFilters(), getDisplayedEmployees(), getRemainingEmployeesCount(), toggleShowAllEmployees(), getServiceRows(): utility helpers for UI behavior and rendering.
 * Lifecycle hooks:
 *   - ngOnInit(): initializes by loading locations, packages, and employees.
 *   - ngOnChanges(changes): reacts to changes of currentLocationLabels, isAdminUser, and isVisible to resync selections or reload data.
 *   - ngOnDestroy(): unsubscribes active subscriptions to avoid memory leaks.
 * @component
 * @selector app-appointments-filter-modal
 * @example
 *   <app-appointments-filter-modal [isVisible]="true" [currentLocationLabels]="labels" (applyFilters)="onApply($event)" (close)="onClose()"></app-appointments-filter-modal>
 * @input boolean isVisible - Controls modal visibility; when true the modal syncs with activeFilters.
 * @input string[] currentLocationLabels - Location labels from the toolbar to pre-select or sync location checkboxes.
 * @input boolean isAdminUser - Flag indicating admin context (changes package loading behavior).
 * @input FilterOptions | null activeFilters - Optional set of active filter values to initialize the modal selections when opened.
 * @output EventEmitter<void> close - Emitted when the modal should be closed (close button, cancel, or backdrop click).
 * @output EventEmitter<FilterOptions> applyFilters - Emitted with the composed FilterOptions when the user applies filters.
 * @output EventEmitter<void> clearFilters - Emitted when the user clears all filter selections.
 */
@Component({
  selector: 'app-appointments-filter-modal',
  standalone: true,
  imports: [CommonModule, Checkbox, ModalFooterActionsComponent],
  templateUrl: './appointments-filter-modal.component.html',
  styleUrl: './appointments-filter-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppointmentsFilterModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isVisible = false;
  @Input() currentLocationLabels: string[] = []; // Current location selections from toolbar
  @Input() isAdminUser = false; // Admin user flag
  @Input() activeFilters: FilterOptions | null = null; // Current active filters
  @Output() close = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<FilterOptions>();
  @Output() clearFilters = new EventEmitter<void>();

  services: FilterItem[] = [];

  employees: FilterItem[] = [];
  showAllEmployees = false;
  employeeDisplayLimit = 8;

  locations: FilterItem[] = [];

  statuses: FilterItem[] = [
    { value: 'completed', label: 'Completed', checked: false },
    { value: 'confirmed', label: 'Confirmed', checked: false },
    { value: 'pending', label: 'Pending', checked: false },
    { value: 'cancelled', label: 'Canceled', checked: false },
  ];

  private locationsSubscription: Subscription = new Subscription();
  private packagesSubscription: Subscription = new Subscription();
  private employeesSubscription: Subscription = new Subscription();

  constructor(
    private profileService: ProfileService,
    private packageService: PackageService,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.loadLocations();
    this.loadPackages();
    this.loadEmployees();
  }

  ngOnDestroy(): void {
    this.locationsSubscription.unsubscribe();
    this.packagesSubscription.unsubscribe();
    this.employeesSubscription.unsubscribe();
  }

  /**
   * Handle Angular SimpleChanges to respond to input updates: sync location selections when currentLocationLabels changes, reload packages when isAdminUser changes, and sync active filters when the modal becomes visible.
   * @example
   * gOnChanges({ currentLocationLabels: { firstChange: false, currentValue: [...] }, isAdminUser: { firstChange: false, currentValue: true }, isVisible: { firstChange: false, currentValue: true } })
   * void
   * @param {SimpleChanges} changes - Object containing the set of changed input properties provided by Angular.
   * @returns {void} No return value; performs side effects to update component state and trigger actions.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to currentLocationLabels input
    if (changes['currentLocationLabels'] && !changes['currentLocationLabels'].firstChange) {
      console.log('📍 Filter Modal - Location labels changed:', this.currentLocationLabels);
      this.syncLocationSelections();
    }

    // Reload packages when admin flag changes to update filtering
    if (changes['isAdminUser'] && !changes['isAdminUser'].firstChange) {
      this.loadPackages();
    }

    // When modal becomes visible, sync with active filters
    if (changes['isVisible'] && changes['isVisible'].currentValue === true && this.activeFilters) {
      this.syncWithActiveFilters();
    }
  }

  /**
   * Syncs the component's services, employees, locations, and statuses checked states with the current activeFilters.
   * @example
   * syncWithActiveFilters()
   * // void
   * @returns {{void}} No return value.
   */
  private syncWithActiveFilters(): void {
    if (!this.activeFilters) {
      return;
    }

    // Sync services
    this.services.forEach(service => {
      service.checked = this.activeFilters!.services?.includes(service.value) || false;
    });

    // Sync employees
    this.employees.forEach(employee => {
      employee.checked = this.activeFilters!.employees?.includes(employee.value) || false;
    });

    // Sync locations
    this.locations.forEach(location => {
      location.checked = this.activeFilters!.locations?.includes(location.value) || false;
    });

    // Sync statuses
    this.statuses.forEach(status => {
      status.checked = this.activeFilters!.statuses?.includes(status.value) || false;
    });
  }

  private loadLocations(): void {
    this.locationsSubscription = subscribeToLocationsForFilterModal(this.profileService, this);
  }

  private syncLocationSelections(): void {
    syncLocationSelectionsUtil(this.locations, this.currentLocationLabels);
  }

  /**
   * Loads and prepares detailing packages into the component's services array and manages the packages subscription based on the current user's role.
   * @example
   * this.loadPackages()
   * undefined (updates this.services asynchronously and manages subscriptions)
   * @returns {void} Performs side effects: subscribes/unsubscribes to package streams and sets this.services with detailing packages.
   **/
  private loadPackages(): void {
    // Unsubscribe from previous subscription if exists
    if (this.packagesSubscription) {
      this.packagesSubscription.unsubscribe();
    }

    if (!this.isAdminUser) {
      // Staff user: use location-based API
      this.packagesSubscription = this.packageService
        .getPackagesForCurrentEmployeeLocation()
        .subscribe({
          next: packages => {
            // Filter for detailing packages only and active packages
            const detailingPackages = packages.filter(
              pkg => pkg.package_status && pkg.type === 'detailing'
            );

            this.services = detailingPackages.map(pkg => ({
              value: pkg.id,
              label: pkg.package_name,
              checked: false,
            }));
          },
          // Leave the Services group empty rather than half-populated when the load fails.
          error: () => {
            this.services = [];
          },
        });
    } else {
      // Admin user: use regular loadPackages API
      this.packageService.loadPackages();

      this.packagesSubscription = this.packageService.packages$.subscribe(apiPackages => {
        // Show all active packages for admin
        const activePackages = apiPackages.filter(
          pkg => pkg.package_status && pkg.type === 'detailing'
        );

        this.services = activePackages.map(pkg => ({
          value: pkg.id,
          label: pkg.package_name,
          checked: false,
        }));
      });
    }
  }

  /**
   * Loads employees for all profile locations, deduplicates and maps them to FilterItem objects, and assigns them to this.employees.
   * @example
   * loadEmployees()
   * undefined
   * @param {{void}} {{none}} - No parameters.
   * @returns {{void}} Updates component state (this.employees) and returns nothing.
   **/
  private loadEmployees(): void {
    // Subscribe to locations to load employees for all locations
    this.employeesSubscription = this.profileService.locations$.subscribe(apiLocations => {
      if (apiLocations && apiLocations.length > 0) {
        // Create an array of API calls for each location
        const employeeRequests = apiLocations.map(location =>
          this.apiService.getEmployeesByLocation(location.id)
        );

        // Execute all requests in parallel
        forkJoin(employeeRequests).subscribe({
          next: responses => {
            // Combine all employees from all locations
            const allEmployees: any[] = [];
            responses.forEach(response => {
              if (response.status && Array.isArray(response.data)) {
                allEmployees.push(...response.data);
              }
            });

            // Remove duplicates based on employee ID and transform to FilterItem
            const uniqueEmployees = allEmployees
              .filter(
                (employee, index, self) => self.findIndex(e => e.id === employee.id) === index
              )
              .map(employee => ({
                value: employee.id,
                label: employee.first_name + ' ' + employee.last_name,
                checked: false,
              }));

            this.employees = uniqueEmployees;
          },
          error: error => {
            console.error('Error loading employees:', error);
            this.employees = [];
          },
        });
      } else {
        this.employees = [];
      }
    });
  }

  onClose(): void {
    this.close.emit();
  }

  onApplyFilters(): void {
    const filters: FilterOptions = {
      services: this.services.filter(item => item.checked).map(item => item.value),
      employees: this.employees.filter(item => item.checked).map(item => item.value),
      locations: this.locations.filter(item => item.checked).map(item => item.value),
      statuses: this.statuses.filter(item => item.checked).map(item => item.value),
    };
    this.applyFilters.emit(filters);
  }

  onClearFilters(): void {
    this.services.forEach(item => (item.checked = false));
    this.employees.forEach(item => (item.checked = false));
    this.locations.forEach(item => (item.checked = false));
    this.statuses.forEach(item => (item.checked = false));
    this.clearFilters.emit();
  }

  onCancel(): void {
    this.close.emit();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  hasActiveFilters(): boolean {
    return (
      this.services.some(item => item.checked) ||
      this.employees.some(item => item.checked) ||
      this.locations.some(item => item.checked) ||
      this.statuses.some(item => item.checked)
    );
  }

  getDisplayedEmployees(): FilterItem[] {
    return this.showAllEmployees
      ? this.employees
      : this.employees.slice(0, this.employeeDisplayLimit);
  }

  getRemainingEmployeesCount(): number {
    return Math.max(0, this.employees.length - this.employeeDisplayLimit);
  }

  toggleShowAllEmployees(): void {
    this.showAllEmployees = !this.showAllEmployees;
  }

  getServiceRows(): FilterItem[][] {
    const rows: FilterItem[][] = [];
    for (let i = 0; i < this.services.length; i += 2) {
      rows.push(this.services.slice(i, i + 2));
    }
    return rows;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
